import { useCallback, useEffect, useRef, useState } from 'react';
import { searchKnowledgeHub } from 'services/confluenceService';
import { KnowledgeHubSearchResult } from 'services/types';

const MIN_QUERY_LENGTH = 2;
const MAX_QUERY_LENGTH = 200;
const DEBOUNCE_MS = 1000;

export interface UseKnowledgeHubSearchResult {
  query: string;
  setQuery: (value: string) => void;
  results: KnowledgeHubSearchResult[];
  totalResults: number;
  loading: boolean;
  error: string | null;
  hasSearched: boolean;
  clear: () => void;
}

/**
 * Debounced Knowledge Hub search backed by GET /knowledge-hub/search.
 * Queries shorter than the API's minimum are ignored so we never send a
 * request that would 422.
 */
export function useKnowledgeHubSearch(limit = 20): UseKnowledgeHubSearchResult {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<KnowledgeHubSearchResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const requestId = useRef(0);

  const clear = useCallback(() => {
    requestId.current += 1;
    setQuery('');
    setResults([]);
    setTotalResults(0);
    setLoading(false);
    setError(null);
    setHasSearched(false);
  }, []);

  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length < MIN_QUERY_LENGTH) {
      requestId.current += 1;
      setResults([]);
      setTotalResults(0);
      setLoading(false);
      setError(null);
      setHasSearched(false);
      return undefined;
    }

    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(null);

    const timer = window.setTimeout(() => {
      searchKnowledgeHub(trimmed.slice(0, MAX_QUERY_LENGTH), limit)
        .then((response) => {
          if (currentRequest !== requestId.current) return;
          setResults(response.results);
          setTotalResults(response.total_results);
          setHasSearched(true);
        })
        .catch((err: unknown) => {
          if (currentRequest !== requestId.current) return;
          setResults([]);
          setTotalResults(0);
          setHasSearched(true);
          setError(err instanceof Error ? err.message : 'Search failed.');
        })
        .finally(() => {
          if (currentRequest !== requestId.current) return;
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query, limit]);

  return { query, setQuery, results, totalResults, loading, error, hasSearched, clear };
}
